import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Task, User } from '../../types/task.types';
import { Avatar } from '../Avatar';
import { XMarkIcon, UserPlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import './TaskAssignmentModal.styles.css';

interface TaskAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: Task | null;
  users: User[];
  onAssign: (taskId: string, userId: string | null, roleName: string | null) => Promise<void>;
}

export const TaskAssignmentModal: React.FC<TaskAssignmentModalProps> = ({
  isOpen,
  onClose,
  task,
  users,
  onAssign
}) => {
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [assignmentType, setAssignmentType] = useState<'user' | 'role'>('user');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedUserId(null);
      setSelectedRole(null);
      setSearchTerm('');
      setIsSubmitting(false);
      setAssignmentType('user');
    }
  }, [isOpen]);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase();
    const email = user.email.toLowerCase();
    const search = searchTerm.toLowerCase();
    return fullName.includes(search) || email.includes(search);
  });

  // Group users by role (move Frédéric Chatelain to bureau group)
  const usersByRole = filteredUsers.reduce((acc, user) => {
    let role = user.role || 'other';

    // Move Frédéric Chatelain to bureau group
    if (user.email === 'frederic.chatelain@chatelainxpert.be' ||
        (user.first_name === 'Frédéric' && user.last_name === 'Chatelain')) {
      role = 'bureau';
    }
    // Skip other admin users (don't display them)
    else if (role === 'admin') {
      return acc;
    }

    if (!acc[role]) {
      acc[role] = [];
    }
    acc[role].push(user);
    return acc;
  }, {} as Record<string, User[]>);

  const handleAssign = async () => {
    if (!task) return;

    if (assignmentType === 'user' && !selectedUserId) {
      alert('Veuillez sélectionner un utilisateur');
      return;
    }

    if (assignmentType === 'role' && !selectedRole) {
      alert('Veuillez sélectionner un rôle');
      return;
    }

    setIsSubmitting(true);
    try {
      await onAssign(
        task.id,
        assignmentType === 'user' ? selectedUserId : null,
        assignmentType === 'role' ? selectedRole : null
      );
      onClose();
    } catch (error) {
      console.error('Error assigning task:', error);
      alert('Erreur lors de l\'assignation de la tâche');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !task) return null;

  const modalContent = (
    <div className="task-assignment-modal-overlay" onClick={onClose}>
      <div className="task-assignment-modal" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="task-assignment-modal-header">
          <h2 className="task-assignment-modal-title">
            <UserPlusIcon className="task-assignment-modal-icon" />
            Assigner la tâche
          </h2>
          <button
            onClick={onClose}
            className="task-assignment-modal-close"
            aria-label="Fermer"
          >
            <XMarkIcon className="task-assignment-modal-close-icon" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="task-assignment-modal-body">
          {/* Task Info */}
          <div className="task-assignment-task-info">
          <div className="task-assignment-task-title">{task.message}</div>
          {task.k_id_affaire && (
            <div className="task-assignment-task-dossier">
              Dossier #{task.k_id_affaire}
              {task.affaire_data && (
                <>
                  {task.affaire_data.encodeur && (
                    <span className="task-assignment-task-meta">
                      • Encodé par: {task.affaire_data.encodeur}
                    </span>
                  )}
                  {task.affaire_data.rv_pris_par && (
                    <span className="task-assignment-task-meta">
                      • RV pris par: {task.affaire_data.rv_pris_par}
                    </span>
                  )}
                </>
              )}
            </div>
          )}
          </div>

          {/* Quick Assign Buttons */}
          {task.affaire_data && (task.affaire_data.encodeur || task.affaire_data.rv_pris_par || task.affaire_data.eval1) && (
            <div className="task-assignment-quick-buttons">
            <div className="task-assignment-quick-title">Assignation rapide</div>
            <div className="task-assignment-quick-grid">
              {task.affaire_data.encodeur && (
                <button
                  className="task-assignment-quick-btn"
                  onClick={() => {
                    // Find user by name
                    const encodeurUser = users.find(u =>
                      `${u.first_name} ${u.last_name}`.toLowerCase() === task.affaire_data!.encodeur.toLowerCase()
                    );
                    if (encodeurUser) {
                      setAssignmentType('user');
                      setSelectedUserId(encodeurUser.id);
                      setSelectedRole(null);
                    }
                  }}
                >
                  <UserPlusIcon className="task-assignment-quick-icon" />
                  <span>Encodeur: {task.affaire_data.encodeur}</span>
                </button>
              )}
              {task.affaire_data.rv_pris_par && (
                <button
                  className="task-assignment-quick-btn"
                  onClick={() => {
                    // Find user by name
                    const rvUser = users.find(u =>
                      `${u.first_name} ${u.last_name}`.toLowerCase() === task.affaire_data!.rv_pris_par.toLowerCase()
                    );
                    if (rvUser) {
                      setAssignmentType('user');
                      setSelectedUserId(rvUser.id);
                      setSelectedRole(null);
                    }
                  }}
                >
                  <UserPlusIcon className="task-assignment-quick-icon" />
                  <span>RV pris par: {task.affaire_data.rv_pris_par}</span>
                </button>
              )}
              {task.affaire_data.eval1 && (
                <button
                  className="task-assignment-quick-btn"
                  onClick={() => {
                    // Find user by name
                    const eval1User = users.find(u =>
                      `${u.first_name} ${u.last_name}`.toLowerCase() === task.affaire_data!.eval1.toLowerCase()
                    );
                    if (eval1User) {
                      setAssignmentType('user');
                      setSelectedUserId(eval1User.id);
                      setSelectedRole(null);
                    }
                  }}
                >
                  <UserPlusIcon className="task-assignment-quick-icon" />
                  <span>Eval1: {task.affaire_data.eval1}</span>
                </button>
              )}
              </div>
            </div>
          )}

          {/* Assignment Type Selector */}
          <div className="task-assignment-type-selector">
          <button
            className={`task-assignment-type-btn ${assignmentType === 'user' ? 'active' : ''}`}
            onClick={() => {
              setAssignmentType('user');
              setSelectedRole(null);
            }}
          >
            Assigner à un utilisateur
          </button>
          <button
            className={`task-assignment-type-btn ${assignmentType === 'role' ? 'active' : ''}`}
            onClick={() => {
              setAssignmentType('role');
              setSelectedUserId(null);
            }}
          >
            Assigner à un rôle
          </button>
        </div>

          {/* Content based on assignment type */}
          <div className="task-assignment-modal-content">
          {assignmentType === 'user' ? (
            <>
              {/* Search */}
              <div className="task-assignment-search">
                <MagnifyingGlassIcon className="task-assignment-search-icon" />
                <input
                  type="text"
                  placeholder="Rechercher un utilisateur..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="task-assignment-search-input"
                />
              </div>

              {/* Users List */}
              <div className="task-assignment-users-list">
                {Object.entries(usersByRole).map(([role, roleUsers]) => {
                  // Skip admin group in display (should already be filtered)
                  if (role === 'admin') return null;

                  return (
                    <div key={role} className="task-assignment-role-group">
                      <div className="task-assignment-role-header">
                        {role === 'bureau' && 'Bureau'}
                        {role === 'expert' && 'Experts'}
                        {role === 'client' && 'Clients'}
                        {role === 'other' && 'Autres'}
                      </div>
                    <div className="task-assignment-users-grid">
                      {roleUsers.map(user => (
                        <button
                          key={user.id}
                          className={`task-assignment-user-card ${selectedUserId === user.id ? 'selected' : ''}`}
                          onClick={() => setSelectedUserId(user.id)}
                        >
                          <Avatar
                            name={`${user.first_name} ${user.last_name}`}
                            size="small"
                          />
                          <div className="task-assignment-user-info">
                            <div className="task-assignment-user-name">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="task-assignment-user-email">
                              {user.email}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            </>
          ) : (
            /* Role Selection */
            <div className="task-assignment-roles-list">
              <button
                className={`task-assignment-role-card ${selectedRole === 'bureau' ? 'selected' : ''}`}
                onClick={() => setSelectedRole('bureau')}
              >
                <div className="task-assignment-role-icon">👥</div>
                <div className="task-assignment-role-info">
                  <div className="task-assignment-role-name">Bureau</div>
                  <div className="task-assignment-role-desc">
                    Assigner à tous les membres du bureau et Frédéric Chatelain
                  </div>
                </div>
              </button>

              <button
                className={`task-assignment-role-card ${selectedRole === 'expert' ? 'selected' : ''}`}
                onClick={() => setSelectedRole('expert')}
              >
                <div className="task-assignment-role-icon">🎓</div>
                <div className="task-assignment-role-info">
                  <div className="task-assignment-role-name">Expert</div>
                  <div className="task-assignment-role-desc">
                    Assigner à tous les experts
                  </div>
                </div>
              </button>
            </div>
          )}
          </div>
        </div>

        {/* Footer */}
        <div className="task-assignment-modal-footer">
          <button
            onClick={onClose}
            className="task-assignment-btn-cancel"
            disabled={isSubmitting}
          >
            Annuler
          </button>
          <button
            onClick={handleAssign}
            className="task-assignment-btn-confirm"
            disabled={isSubmitting || (assignmentType === 'user' ? !selectedUserId : !selectedRole)}
          >
            {isSubmitting ? 'En cours...' : 'Assigner'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};